import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast, Toaster } from 'sonner';
import { 
  Gift, Save, RefreshCcw, Percent, Phone, Mail, 
  Clock, Eye, EyeOff, Plus, Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminPromoPopupPage = () => {
  const [settings, setSettings] = useState({
    enabled: true,
    discount_percent: 25,
    headline: 'Exclusive Offer!',
    subheadline: 'Book a Demo Today & Save Big!',
    description: 'Get an instant discount on both Development and Hosting Support',
    benefits: [
      'Save up to $8,000+ on development costs',
      'Stop paying Subscription - own it forever',
      'Your Branded NDIS Web And Mobile App',
      'No hidden fees or per-user charges'
    ],
    cta_text: 'Claim Your Discount',
    phone: '1800 951 GET',
    email: 'info@getcare360.com.au',
    urgency_text: "Don't miss out! This offer is available for a limited time only.",
    show_on_features_page: true,
    delay_seconds: 3
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/promo-popup`);
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/cms/promo-popup`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      
      if (res.ok) {
        toast.success('Settings saved successfully!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addBenefit = () => {
    setSettings({
      ...settings,
      benefits: [...settings.benefits, '']
    });
  };

  const removeBenefit = (index) => {
    const newBenefits = settings.benefits.filter((_, i) => i !== index);
    setSettings({ ...settings, benefits: newBenefits });
  };

  const updateBenefit = (index, value) => {
    const newBenefits = [...settings.benefits];
    newBenefits[index] = value;
    setSettings({ ...settings, benefits: newBenefits });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-text-secondary">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <Gift className="text-heritage-orange" />
            Promo Popup Settings
          </h1>
          <p className="text-text-secondary">Configure the exclusive offer popup</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadSettings} variant="outline">
            <RefreshCcw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button onClick={saveSettings} disabled={saving} className="bg-care-teal hover:bg-care-teal/90">
            <Save size={16} className="mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Main Settings */}
        <Card className="border-border-line">
          <CardHeader>
            <CardTitle className="text-lg">Popup Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-page-bg rounded-lg">
              <div className="flex items-center gap-3">
                {settings.enabled ? (
                  <Eye className="text-green-600" size={20} />
                ) : (
                  <EyeOff className="text-gray-400" size={20} />
                )}
                <div>
                  <p className="font-medium text-text-primary">Popup Status</p>
                  <p className="text-sm text-text-secondary">
                    {settings.enabled ? 'Popup is visible to visitors' : 'Popup is hidden'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  settings.enabled ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    settings.enabled ? 'left-8' : 'left-1'
                  }`}
                />
              </button>
            </div>

            {/* Discount Percentage */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                <Percent size={14} className="inline mr-1" />
                Discount Percentage
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={settings.discount_percent}
                  onChange={(e) => setSettings({ ...settings, discount_percent: parseInt(e.target.value) || 0 })}
                  className="w-24"
                  min="1"
                  max="100"
                />
                <span className="text-2xl font-bold text-heritage-orange">%</span>
                <span className="text-text-secondary">OFF</span>
              </div>
            </div>

            {/* Delay */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                <Clock size={14} className="inline mr-1" />
                Popup Delay (seconds)
              </label>
              <Input
                type="number"
                value={settings.delay_seconds}
                onChange={(e) => setSettings({ ...settings, delay_seconds: parseInt(e.target.value) || 3 })}
                className="w-24"
                min="0"
                max="30"
              />
            </div>

            {/* Show on Features Page */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="show_features"
                checked={settings.show_on_features_page}
                onChange={(e) => setSettings({ ...settings, show_on_features_page: e.target.checked })}
                className="w-4 h-4 rounded"
              />
              <label htmlFor="show_features" className="text-sm text-text-primary">
                Always show on Features & Pricing page
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Content Settings */}
        <Card className="border-border-line">
          <CardHeader>
            <CardTitle className="text-lg">Content</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Headline</label>
              <Input
                value={settings.headline}
                onChange={(e) => setSettings({ ...settings, headline: e.target.value })}
                placeholder="Exclusive Offer!"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Subheadline</label>
              <Input
                value={settings.subheadline}
                onChange={(e) => setSettings({ ...settings, subheadline: e.target.value })}
                placeholder="Book a Demo Today & Save Big!"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Description</label>
              <Input
                value={settings.description}
                onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                placeholder="Get an instant discount..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">CTA Button Text</label>
              <Input
                value={settings.cta_text}
                onChange={(e) => setSettings({ ...settings, cta_text: e.target.value })}
                placeholder="Claim Your Discount"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Urgency Text</label>
              <Input
                value={settings.urgency_text}
                onChange={(e) => setSettings({ ...settings, urgency_text: e.target.value })}
                placeholder="Don't miss out!..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Benefits List */}
        <Card className="border-border-line">
          <CardHeader>
            <CardTitle className="text-lg">Benefits List</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {settings.benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="text-care-teal font-bold">{index + 1}.</span>
                <Input
                  value={benefit}
                  onChange={(e) => updateBenefit(index, e.target.value)}
                  placeholder="Enter benefit..."
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeBenefit(index)}
                  className="text-red-500 hover:bg-red-50"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
            <Button variant="outline" onClick={addBenefit} className="w-full">
              <Plus size={14} className="mr-2" />
              Add Benefit
            </Button>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="border-border-line">
          <CardHeader>
            <CardTitle className="text-lg">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                <Phone size={14} className="inline mr-1" />
                Phone Number
              </label>
              <Input
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                placeholder="1800 951 GET"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                <Mail size={14} className="inline mr-1" />
                Email Address
              </label>
              <Input
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                placeholder="info@getcare360.com.au"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview */}
      <Card className="border-border-line">
        <CardHeader>
          <CardTitle className="text-lg">Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gradient-to-r from-tech-blue via-primary-navy to-care-teal p-6 rounded-xl text-white max-w-md mx-auto">
            <div className="text-center mb-4">
              <Gift className="mx-auto mb-2" size={32} />
              <h3 className="text-xl font-bold">{settings.headline}</h3>
            </div>
            <div className="bg-heritage-orange text-white text-center py-3 rounded-lg mb-4">
              <span className="text-3xl font-bold">{settings.discount_percent}% OFF</span>
            </div>
            <p className="text-white/80 text-sm text-center mb-3">{settings.description}</p>
            <ul className="space-y-1 text-sm mb-4">
              {settings.benefits.slice(0, 3).map((b, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="text-care-teal">✓</span> {b}
                </li>
              ))}
            </ul>
            <button className="w-full bg-heritage-orange hover:bg-heritage-orange/90 py-3 rounded-lg font-semibold">
              {settings.cta_text}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPromoPopupPage;
